import { useEffect, useRef, useState } from 'react';

export type PollStatus = 'polling' | 'success' | 'timeout';

// Polls `fetcher` every `intervalMs` until `isDone` passes (→ 'success') or
// `timeoutMs` elapses (→ 'timeout'). Transient errors are ignored and retried.
export function usePolling<T>({
  enabled,
  fetcher,
  isDone,
  intervalMs,
  timeoutMs,
}: {
  enabled: boolean;
  fetcher: () => Promise<T>;
  isDone: (result: T) => boolean;
  intervalMs: number;
  timeoutMs: number;
}) {
  const [status, setStatus] = useState<PollStatus>('polling');
  const [data, setData] = useState<T | null>(null);

  // Keep latest callbacks so a re-render doesn't restart the polling loop.
  const callbacks = useRef({ fetcher, isDone });
  callbacks.current = { fetcher, isDone };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + timeoutMs;
    setStatus('polling');

    const poll = async () => {
      try {
        const result = await callbacks.current.fetcher();
        if (active && callbacks.current.isDone(result)) {
          setData(result);
          setStatus('success');
          return;
        }
      } catch {
        // ignore transient errors and keep polling until the deadline
      }
      if (!active) {
        return;
      }
      if (Date.now() >= deadline) {
        setStatus('timeout');
      } else {
        timer = setTimeout(poll, intervalMs);
      }
    };

    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [enabled, intervalMs, timeoutMs]);

  return { status, data };
}
